import React, { useEffect, useRef } from "react";
import { Box, Button, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { Bot, Lock, Mail, Paperclip, Sparkles, UserRound } from "lucide-react";
import { openAttachment } from "./api";
import { MessageTicks, eventText, fileSize } from "./ui";
import { PersonAvatar, T, TYPE, formatDateTime } from "../user/complaints/shared";

// Conversation renderer used by the ticket page and the live chat. Customer on
// the left; agent / AI / system on the right; internal notes and AI copilot
// suggestions are visibly marked and never mistaken for customer-facing text.

const bubbleStyle = (m) => {
  if (m.visibility === "internal" && m.senderType === "AI") return { bg: "#EFF8FF", border: "#B2DDFF", align: "flex-end" };
  if (m.visibility === "internal") return { bg: "#FFFAEB", border: "#FEDF89", align: "flex-end" };
  if (m.senderType === "CUSTOMER") return { bg: "#FFFFFF", border: T.border, align: "flex-start" };
  if (m.senderType === "AI") return { bg: "#F4EBFF", border: "#D9D6FE", align: "flex-end" };
  if (m.senderType === "SYSTEM") return { bg: "#F9FAFB", border: T.border, align: "center" };
  return { bg: T.brandSoft, border: T.brandBorder, align: "flex-end" };
};

const SenderIcon = ({ m }) => {
  if (m.senderType === "AI") return m.visibility === "internal" ? <Sparkles size={14} color="#026AA2" /> : <Bot size={14} color="#5925DC" />;
  if (m.senderType === "CUSTOMER") return <UserRound size={14} color={T.inkSecondary} />;
  return null;
};

const Attachments = ({ list }) =>
  list?.length ? (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
      {list.map((a) =>
        a && a._id ? (
          <Chip key={a._id} size="small" icon={<Paperclip size={12} />} label={`${a.fileName} · ${fileSize(a.size || 0)}`} onClick={() => openAttachment(a._id)} sx={{ maxWidth: 260, bgcolor: "#fff", border: `1px solid ${T.border}` }} />
        ) : null
      )}
    </Stack>
  ) : null;

export const MessageItem = ({ m }) => {
  if (m.type === "event") {
    return (
      <Box sx={{ textAlign: "center", my: 1 }}>
        <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>
          {eventText(m.event)} · {formatDateTime(m.createdAt)}
        </Typography>
      </Box>
    );
  }
  const s = bubbleStyle(m);
  if (m.senderType === "SYSTEM" && m.type !== "email") {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 1 }}>
        <Box sx={{ px: 1.5, py: 0.75, borderRadius: "10px", bgcolor: s.bg, border: `1px dashed ${s.border}`, maxWidth: "80%" }}>
          <Typography sx={{ fontSize: TYPE.micro, color: T.inkSecondary, whiteSpace: "pre-wrap" }}>{m.content}</Typography>
        </Box>
      </Box>
    );
  }
  const isEmail = m.type === "email";
  const name = m.sender?.name || (m.senderType === "AI" ? "AI Assistant" : m.senderType === "CUSTOMER" ? "Customer" : "Support");
  return (
    <Box sx={{ display: "flex", justifyContent: s.align, my: 1.25 }}>
      <Stack direction={s.align === "flex-start" ? "row" : "row-reverse"} spacing={1} sx={{ maxWidth: "82%", minWidth: 0 }} alignItems="flex-start">
        <PersonAvatar name={name} size={28} />
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={0.75} alignItems="center" justifyContent={s.align === "flex-start" ? "flex-start" : "flex-end"} sx={{ mb: 0.4 }}>
            <SenderIcon m={m} />
            <Typography sx={{ fontSize: TYPE.micro, fontWeight: 700, color: T.ink }}>{name}</Typography>
            {m.visibility === "internal" && (
              <Chip size="small" icon={<Lock size={10} />} label={m.senderType === "AI" ? "AI suggestion · internal" : "Internal note"} sx={{ height: 18, fontSize: "0.62rem", bgcolor: m.senderType === "AI" ? "#D1E9FF" : "#FEF0C7" }} />
            )}
            {isEmail && <Tooltip title={`${m.email?.direction === "inbound" ? "From" : "To"}: ${m.email?.direction === "inbound" ? m.email?.from : (m.email?.to || []).join(", ")}`}><Mail size={12} color={T.inkMuted} /></Tooltip>}
            <Typography sx={{ fontSize: "0.68rem", color: T.inkMuted }}>{formatDateTime(m.createdAt)}</Typography>
            {m.senderType !== "CUSTOMER" && m.visibility === "public" && <MessageTicks status={m.status} />}
          </Stack>
          <Box sx={{ px: 1.75, py: 1.25, borderRadius: "12px", bgcolor: s.bg, border: `1px solid ${s.border}`, overflowWrap: "anywhere" }}>
            {isEmail && m.email?.subject && <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted, mb: 0.5 }}>{m.email.subject}</Typography>}
            {/* content is plain text (sanitised on the server); rendered as text, never as HTML */}
            <Typography sx={{ fontSize: TYPE.body, color: T.ink, whiteSpace: "pre-wrap", lineHeight: 1.55 }}>{m.content}</Typography>
            <Attachments list={m.attachments} />
            {m.status === "failed" && <Typography sx={{ fontSize: TYPE.micro, color: "#B42318", mt: 0.5 }}>Not delivered: {m.failedReason || "send failed"}</Typography>}
            {m.senderType === "AI" && m.ai?.confidence != null && (
              <Typography sx={{ fontSize: "0.65rem", color: T.inkMuted, mt: 0.5 }}>
                Confidence {Math.round(m.ai.confidence * 100)}%{m.ai.sourceArticleIds?.length ? ` · ${m.ai.sourceArticleIds.length} KB source(s)` : ""}
              </Typography>
            )}
          </Box>
        </Box>
      </Stack>
    </Box>
  );
};

export default function MessageThread({ messages, hasMore, onLoadOlder, loadingOlder, typing, autoScroll = true, height }) {
  const bottom = useRef(null);
  const lastId = messages.length ? messages[messages.length - 1]._id : null;
  useEffect(() => {
    if (autoScroll) bottom.current?.scrollIntoView({ block: "end" });
  }, [lastId, autoScroll, typing]);

  return (
    <Box
      sx={{
        px: 2.5,
        py: 2,
        overflowY: "auto",
        height: height || "100%",
        flex: height ? undefined : 1,
        minHeight: 0,
        bgcolor: "#FCFCFD",
        "&::-webkit-scrollbar": { width: 6 },
        "&::-webkit-scrollbar-thumb": { bgcolor: "#CBD5E1", borderRadius: 3 },
        "&::-webkit-scrollbar-thumb:hover": { bgcolor: "#94A3B8" },
      }}
    >
      {hasMore && (
        <Box sx={{ textAlign: "center", mb: 1 }}>
          <Button size="small" onClick={onLoadOlder} disabled={loadingOlder}>{loadingOlder ? "Loading…" : "Load earlier messages"}</Button>
        </Box>
      )}
      {!messages.length && <Typography sx={{ textAlign: "center", color: T.inkMuted, fontSize: TYPE.small, py: 6 }}>No messages yet</Typography>}
      {messages.map((m) => <MessageItem key={m._id} m={m} />)}
      {typing && (
        <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted, fontStyle: "italic", mt: 1 }}>{typing}</Typography>
      )}
      <div ref={bottom} />
    </Box>
  );
}

/** Merge a realtime message into a sorted, de-duplicated list. */
export const upsertMessage = (list, m) => {
  const i = list.findIndex((x) => String(x._id) === String(m._id) || (m.clientMsgId && x.clientMsgId === m.clientMsgId));
  if (i >= 0) {
    const next = [...list];
    next[i] = { ...next[i], ...m };
    return next;
  }
  return [...list, m].sort((a, b) => (String(a._id) < String(b._id) ? -1 : 1));
};
